/**
 * PackageAndAccessoriesStep — E-105 Phase 2 / ADR-103 Addendum 2 / ADR-108
 * Phase A. The 6th accordion panel of the rental wizard.
 *
 * Per-equipment cards show:
 *   - Equipment name + currently attached package selections (chips)
 *   - "Csomag hozzáadása" dropdown — fetched per-equipment with
 *     `usePackagesForEquipment(equipmentId)`
 *   - Soft-guard toast (Winston-J3) when the same package is being added
 *     a second time to the same equipment — but the duplication is allowed
 *
 * Cart cascade cleanup is owned by the wizard-store: removing equipment from
 * the cart drops its packageSelections automatically. This component only
 * reads from / writes to the normalized `packageSelections[]` slice.
 *
 * Telemetry: every selection create/remove emits a
 * `rental.package_selection.created/.removed` event (lib/telemetry.ts).
 */

import type { Package } from '@/api/packages';
import { Button, Card, CardContent } from '@/components/ui';
import { useEffectivePackageByEquipment } from '@/hooks/use-effective-package-by-equipment';
import { usePackagesForEquipment } from '@/hooks/use-packages';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { Badge, PackageBadge, Skeleton } from '@kgc/ui';
import { useState } from 'react';
import type { CartItem } from '../types';
import { useWizardStore } from '../wizard-store';

export function PackageAndAccessoriesStep() {
  const { t } = useTranslation();
  const cart = useWizardStore(s => s.cart);

  if (cart.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        {t('rental.wizard.packages.emptyCart')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('rental.wizard.packages.intro')}</p>
      {cart.map(cartItem => (
        <EquipmentPackageCard key={cartItem.id} cartItem={cartItem} />
      ))}
    </div>
  );
}

interface EquipmentPackageCardProps {
  cartItem: CartItem;
}

function EquipmentPackageCard({ cartItem }: EquipmentPackageCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const equipmentId = cartItem.equipment.id;
  const selections = useWizardStore(s => s.getSelectionsForEquipment(equipmentId));
  const hasDuplicate = useWizardStore(s => s.hasDuplicateSelection);
  const addPackageSelection = useWizardStore(s => s.addPackageSelection);
  const removePackageSelection = useWizardStore(s => s.removePackageSelection);
  const { packages, isLoading } = usePackagesForEquipment(equipmentId);

  const handleAdd = (pkg: Package) => {
    if (hasDuplicate(equipmentId, pkg.id)) {
      // Soft-guard (Winston J-3): warn but allow — "2 készlet védőfelszerelés" is valid.
      toast({
        title: t('rental.wizard.packages.duplicateTitle'),
        description: t('rental.wizard.packages.duplicateDesc', { name: pkg.name }),
      });
    }
    addPackageSelection({
      equipmentId,
      packageId: pkg.id,
      packageName: pkg.name,
      packageCode: pkg.packageCode,
    });
  };

  const { effectivePackage, isLoading: isResolverLoading } = useEffectivePackageByEquipment(
    equipmentId,
    'RENTAL'
  );

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <div className="font-semibold text-sm text-foreground">{cartItem.equipment.name}</div>
            <div className="text-xs text-muted-foreground">
              {cartItem.equipment.equipmentCode ?? cartItem.equipment.serialNumber} ·{' '}
              {t('rental.wizard.packages.qtyLabel', { qty: cartItem.quantity })}
            </div>
          </div>
        </div>

        {/* E-105 Phase 2 S10 MVP — resolved effective package per equipment */}
        <EquipmentResolvedBanner
          effectivePackage={effectivePackage}
          isLoading={isResolverLoading}
        />

        {selections.length > 0 && (
          <ul className="flex flex-wrap gap-2" data-testid="selection-chip-list">
            {selections.map(sel => (
              <li
                key={sel.selectionId}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs',
                  'bg-primary/10 border border-primary/30 text-foreground'
                )}
              >
                <span className="font-medium">{sel.packageName}</span>
                <span className="text-muted-foreground">({sel.packageCode})</span>
                <button
                  type="button"
                  onClick={() => removePackageSelection(sel.selectionId, 'user-action')}
                  aria-label={t('rental.wizard.packages.removeAria', { name: sel.packageName })}
                  className="ml-1 rounded-full hover:bg-primary/20 px-1 text-muted-foreground hover:text-foreground"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <PackagePicker
          isLoading={isLoading}
          packages={packages}
          onAdd={handleAdd}
          emptyLabel={t('rental.wizard.packages.noneAvailable')}
          addLabel={t('rental.wizard.packages.addPackage')}
        />
      </CardContent>
    </Card>
  );
}

interface PackagePickerProps {
  isLoading: boolean;
  packages: Package[];
  onAdd: (pkg: Package) => void;
  emptyLabel: string;
  addLabel: string;
}

function PackagePicker({ isLoading, packages, onAdd, emptyLabel, addLabel }: PackagePickerProps) {
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground italic" role="status" aria-live="polite">
        …
      </div>
    );
  }

  if (packages.length === 0) {
    return <div className="text-xs text-muted-foreground italic">{emptyLabel}</div>;
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        aria-expanded={false}
      >
        + {addLabel}
      </Button>
    );
  }

  return (
    <div
      className="border rounded-md bg-card max-h-56 overflow-y-auto"
      role="listbox"
      aria-label={addLabel}
    >
      {packages.map(pkg => (
        <button
          key={pkg.id}
          type="button"
          role="option"
          aria-selected="false"
          onClick={() => {
            onAdd(pkg);
            setOpen(false);
          }}
          className="block w-full text-left px-3 py-2 hover:bg-muted/50 border-b last:border-0 motion-safe:transition"
        >
          <div className="text-sm font-medium text-foreground">{pkg.name}</div>
          <div className="text-xs text-muted-foreground">{pkg.packageCode}</div>
        </button>
      ))}
    </div>
  );
}

function EquipmentResolvedBanner({
  effectivePackage,
  isLoading,
}: {
  effectivePackage: import('@/api/package-resolver').ResolvedPackageDto | null | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return <Skeleton className="h-8 w-full" data-testid="resolved-package-loading" />;
  }

  if (!effectivePackage) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-dashed border-muted-foreground/30 px-3 py-2 text-xs text-muted-foreground"
        data-testid="resolved-package-none"
      >
        <Badge variant="outline" className="text-[10px]">
          {t('rental.resolvedPackage.none')}
        </Badge>
        <span>{t('rental.resolvedPackage.noneDesc')}</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2"
      data-testid="resolved-package-info"
    >
      <PackageBadge
        packageType={
          effectivePackage.type as
            | 'RENTAL'
            | 'SERVICE'
            | 'SALES'
            | 'MIXED'
            | 'PROMOTIONAL'
            | 'FRANCHISE'
        }
        size="sm"
      />
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">{effectivePackage.name}</div>
        <div className="text-xs text-muted-foreground font-mono">
          {effectivePackage.packageCode}
        </div>
      </div>
    </div>
  );
}
